import React, { useState, useRef, useEffect } from "react";
import { Checkbox, styled, SvgIcon, Typography, Tooltip } from "@mui/material";
import { ChevronDown, ChevronRight, Folder, Edit, AddNew, Delete } from "orion-components/CBComponents/Icons";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import EditGroupNode from "./EditGroupNode";
import SpriteIcon from "orion-components/SharedComponents/SpriteIcon/SpriteIcon";
import { useDispatch, useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { TargetingIcon } from "orion-components/SharedComponents";
import { createUserFeedback } from "orion-components/Dock/Actions/index.js";
import { canManageByApplication } from "orion-components/Profiles/Selectors";
import PropTypes from "prop-types";
import { getTranslation } from "orion-components/i18n";
import { deleteDeviceGroup } from "../../../GlobalData/DeviceGroups/actions";
import {
	createDeviceDragPayload,
	DEVICE_PLACEMENT_POINTER_DROP_EVENT,
	getDeviceGroupDropDataset,
	getDeviceGroupDropTargetFromElement,
	isPlaceableDeviceEntity
} from "../deviceTreeDnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

const CustomizedCheckBox = styled(Checkbox)(() => ({
	width: "17px !important",
	height: "17px !important",
	marginRight: "12.6px",
	padding: "0px !important",
	marginLeft: "-2px !important"
}));

// This function is used to apply the margin property to nodes that have children and the root "All Devices" node.
const getNodeIndentation = level => 10 * (level ?? 0);
// If the node does not have children, the above function is used to adjust the spacing where the chevron would be and modify its actual margin accordingly.
const getChildNodeIndentation = (parentLevel, useCustomMargin, customMargin) =>
	useCustomMargin ? customMargin : 34 + parentLevel * 10;

const styles = {
	targetingIcon: dir => ({
		...(dir === "ltr" && { paddingLeft: 0 }),
		...(dir === "rtl" && { paddingRight: 0 })
	}),
	container: (node, hoveredNodeId, selectedDevice, displayChevrons) => ({
		alignItems: "center",
		display: "flex",
		height: 32,
		...((node?.children?.length === 0 || !node?.children) && node?.level !== 0
			? { marginLeft: getChildNodeIndentation(node?.parentLevel, node?.useCustomMargin, node?.customMargin) }
			: { marginLeft: getNodeIndentation(node?.level) }),
		backgroundColor: hoveredNodeId === node?.id || selectedDevice?.id === node?.id ? "#5D646A" : "transparent",
		borderRadius: 4, // Optional for better styling,
		width: "100%",
		...(!node?.children && { paddingLeft: 6 }),
		...(!displayChevrons && { paddingLeft: 10 }),
		...(node?.isCompositeDevice && node?.children.length === 0 ? { paddingLeft: 6 } : {}) //When a composite device has no children, this padding is applied to fill the chevron space.
	}),
	chevron: dir => ({
		...(dir === "ltr" && { marginLeft: 7.48, marginRight: 12.6 }),
		...(dir === "rtl" && { marginRight: 7.48, marginLeft: 12.6 }),
		width: 10.5
	}),
	checkbox: (node, dir) => ({
		...(node?.children && node.children.length === 0
			? { ...(dir === "ltr" && { marginLeft: 7.48 }), ...(dir === "rtl" && { marginRight: 7.48 }) }
			: {})
	}),
	folder: (node, dir) => ({
		paddingTop: 10,
		...(dir === "rtl" && { marginLeft: 10 }),
		...(node?.children && node.children.length === 0
			? { ...(dir === "ltr" && { marginLeft: 7.48 }), ...(dir === "rtl" && { marginRight: 7.48 }) }
			: {})
	}),
	svgIconWrapper: {
		width: 24,
		height: 24
	},
	spriteIcon: (node, dir) => ({
		width: 20,
		height: 20,
		backgroundColor: node?.deviceStateColor || "#6A6C70",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		borderRadius: "5px",
		...(dir === "ltr" && { marginRight: 12 }),
		...(dir === "rtl" && { marginLeft: 12 }),
		flexShrink: 0
	}),
	nodeOperations: dir => ({
		...(dir === "ltr" && { marginLeft: "auto" }),
		...(dir === "rtl" && { marginRight: "auto" }),
		marginRight: 11.9,
		display: "flex",
		alignItems: "center"
	}),
	editButton: dir => ({
		...(dir === "ltr" && { marginRight: 13.63 }),
		...(dir === "rtl" && { marginLeft: 13.63 })
	}),
	dragHandle: dir => ({
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: 18,
		height: 18,
		color: "#8B8B8B",
		cursor: "grab",
		flexShrink: 0,
		userSelect: "none",
		WebkitUserDrag: "element",
		...(dir === "ltr" && { marginRight: 8 }),
		...(dir === "rtl" && { marginLeft: 8 })
	}),
	dragHandleIcon: {
		pointerEvents: "none",
		userSelect: "none",
		display: "flex",
		alignItems: "center",
		justifyContent: "center"
	},
	placementPendingIndicator: dir => ({
		width: 8,
		height: 8,
		borderRadius: "50%",
		backgroundColor: "#35B7F3",
		boxShadow: "0 0 0 2px rgba(53, 183, 243, 0.22)",
		flexShrink: 0,
		...(dir === "ltr" && { marginRight: 8 }),
		...(dir === "rtl" && { marginLeft: 8 })
	})
};

const DRAG_GHOST_BASE_STYLES = {
	position: "fixed",
	left: "0px",
	top: "0px",
	transform: "translate(-9999px, -9999px)",
	pointerEvents: "none",
	zIndex: "99999",
	background: "rgba(58, 64, 70, 0.95)",
	color: "#FFFFFF",
	border: "1px solid #5D646A",
	borderRadius: "6px",
	padding: "6px 10px",
	fontSize: "14px",
	maxWidth: "260px",
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis"
};

const createDragGhostElement = label => {
	const ghost = document.createElement("div");
	ghost.textContent = label || "Device";
	Object.assign(ghost.style, DRAG_GHOST_BASE_STYLES);
	return ghost;
};

const RenderLabelWithIcon = ({
	node,
	isExpanded,
	hoveredNodeId,
	setHoveredNodeId,
	selectedNodeForEdit,
	setSelectedNodeForEdit,
	addOrUpdateNodeLabel,
	addNewNode,
	readOnly,
	addToDeviceGroup,
	getSelectedNodes,
	isForFacilitiesFloorPlan,
	selectedDevice,
	setSelectedDevice,
	displayChevrons,
	getSelectedMenuItem,
	allowDeviceSelection,
	parentCount,
	onDeviceDropToGroup,
	onDropTargetHoverChange,
	onChevronToggle,
	enableDevicePlacementDrag,
	enableDeviceGroupDrag
}) => {
	const [isGroupSelected, setIsGroupSelected] = useState(false);
	const dir = useSelector(state => getDir(state));
	const compositeDevices = useSelector(state => state.globalData?.compositeDevices);
	const justDraggedRef = useRef(false);
	const pointerDragRef = useRef(null);
	const pointerDragGhostRef = useRef(null);

	const dispatch = useDispatch();

	const handleEditClick = event => {
		event.stopPropagation();
		setSelectedNodeForEdit(node?.id);
	};

	const clearSelectedNodeForEdit = (event, selectedNode) => {
		// Prevent the event from propagating to parent elements
		event.stopPropagation();

		// Optional: Prevent the default behavior if the event is a form submission or a link click
		event.preventDefault();
		setSelectedNodeForEdit(null);
		if (selectedNode?.isTemporary || !selectedNode?.name?.trim()) {
			dispatch(deleteDeviceGroup(selectedNode.id));
		}
	};

	const handleAddNewClick = event => {
		event.stopPropagation();
		if (parentCount < 9) {
			addNewNode(node?.id);
		} else {
			dispatch(
				createUserFeedback(getTranslation("global.deviceManager.deviceTree.maxNestedNodesReached"), null, true)
			);
		}
	};

	const selectDeviceGroups = e => {
		e.preventDefault();
		e.stopPropagation();

		setIsGroupSelected(!isGroupSelected);
		getSelectedNodes(node?.id);
	};

	const canManageCamerasApp = useSelector(state => canManageByApplication(state, "cameras-app", "manage"));
	const pendingDevicePlacements = useSelector(state => [
		...(state.appManager?.listPanel?.pendingDevicePlacements || []),
		...(state.floorPlan?.pendingDevicePlacements || [])
	]);
	const canPlacementDrag =
		enableDevicePlacementDrag && !readOnly && (canManageCamerasApp || isForFacilitiesFloorPlan);
	const canGroupDrag = enableDeviceGroupDrag && !readOnly && canManageCamerasApp && !isForFacilitiesFloorPlan;
	const canDragDrop = canPlacementDrag || canGroupDrag;
	const isDraggableDevice = canDragDrop && isPlaceableDeviceEntity(node);
	const hasPendingPlacement = pendingDevicePlacements.some(
		placement => placement?.entityId === node?.id && placement?.entityType === node?.entityType
	);
	const dragHandleTitle = getTranslation("global.deviceManager.deviceTree.dragDeviceTooltip");
	const fallbackDragPayload = isDraggableDevice
		? createDeviceDragPayload({
				source: "tree",
				entityId: node?.id,
				entityType: node?.entityType,
				sourceGroupId: node?.parentGroupId,
				name: node?.name,
				feedId: node?.feedId,
				entityData: node?.entityData,
				icon: node?.icon,
				deviceStateColor: node?.deviceStateColor,
				deviceType: node?.deviceType
			})
		: null;

	const cleanupPointerDragListeners = () => {
		const moveListener = pointerDragRef.current?.listeners?.move;
		const upListener = pointerDragRef.current?.listeners?.up;

		if (moveListener) {
			window.removeEventListener("pointermove", moveListener);
		}

		if (upListener) {
			window.removeEventListener("pointerup", upListener);
			window.removeEventListener("pointercancel", upListener);
		}

		if (pointerDragRef.current?.listeners) {
			pointerDragRef.current.listeners = null;
		}
	};

	const removePointerDragGhost = () => {
		if (!pointerDragGhostRef.current) return;
		pointerDragGhostRef.current.remove();
		pointerDragGhostRef.current = null;
	};

	const createPointerDragGhost = payload => {
		removePointerDragGhost();
		const ghost = createDragGhostElement(payload?.name);
		document.body.appendChild(ghost);
		pointerDragGhostRef.current = ghost;
	};

	const updatePointerDragGhostPosition = (clientX, clientY) => {
		if (!pointerDragGhostRef.current) return;
		pointerDragGhostRef.current.style.transform = `translate(${clientX + 14}px, ${clientY + 14}px)`;
	};

	function handleHandlePointerMove(event) {
		if (!pointerDragRef.current) return;
		const dx = Math.abs(event.clientX - pointerDragRef.current.startX);
		const dy = Math.abs(event.clientY - pointerDragRef.current.startY);
		if (dx > 4 || dy > 4) {
			pointerDragRef.current.moved = true;
			if (!pointerDragGhostRef.current) {
				createPointerDragGhost(pointerDragRef.current.payload);
			}
			updatePointerDragGhostPosition(event.clientX, event.clientY);
			const targetElement = document.elementFromPoint(event.clientX, event.clientY);
			const dropTarget = getDeviceGroupDropTargetFromElement(targetElement);
			onDropTargetHoverChange?.(dropTarget?.targetGroupId || null);
		}
	}

	function handleHandlePointerUp(event) {
		if (!pointerDragRef.current) return;
		const dragState = pointerDragRef.current;
		cleanupPointerDragListeners();
		pointerDragRef.current = null;
		removePointerDragGhost();
		onDropTargetHoverChange?.(null);

		if (!dragState.moved) {
			return;
		}

		const targetElement = document.elementFromPoint(event.clientX, event.clientY);
		const dropTarget = getDeviceGroupDropTargetFromElement(targetElement);
		const droppedInsideDeviceTree = !!targetElement?.closest?.("[data-device-tree-root='true']");

		if (dropTarget?.targetGroupId && dragState.payload && onDeviceDropToGroup) {
			onDeviceDropToGroup(dropTarget.targetGroupId, dragState.payload);
			justDraggedRef.current = true;
			window.setTimeout(() => {
				justDraggedRef.current = false;
			}, 150);
			return;
		}

		if (droppedInsideDeviceTree) {
			return;
		}

		if (canPlacementDrag && dragState.payload) {
			window.dispatchEvent(
				new CustomEvent(DEVICE_PLACEMENT_POINTER_DROP_EVENT, {
					detail: {
						payload: dragState.payload,
						clientX: event.clientX,
						clientY: event.clientY
					}
				})
			);
		}
	}

	const handleDeviceHandlePointerDown = event => {
		if (!isDraggableDevice || !fallbackDragPayload) {
			return;
		}
		if (event.button !== 0) {
			return;
		}
		const pointerListeners = {
			move: handleHandlePointerMove,
			up: handleHandlePointerUp
		};

		pointerDragRef.current = {
			startX: event.clientX,
			startY: event.clientY,
			moved: false,
			payload: fallbackDragPayload,
			listeners: pointerListeners
		};

		window.addEventListener("pointermove", pointerListeners.move);
		window.addEventListener("pointerup", pointerListeners.up);
		window.addEventListener("pointercancel", pointerListeners.up);
	};

	const handleDragHandlePointerDown = event => {
		event.preventDefault();
		event.stopPropagation();
		handleDeviceHandlePointerDown(event);
	};

	const handleDragHandleClick = event => {
		event.stopPropagation();
	};

	const handleChevronClick = event => {
		event.preventDefault();
		event.stopPropagation();
		onChevronToggle?.(node?.id);
	};

	useEffect(() => {
		return () => {
			cleanupPointerDragListeners();
			removePointerDragGhost();
			onDropTargetHoverChange?.(null);
		};
	}, []);

	const handleRemove = node => {
		const menuItem =
			node?.isDevice && node?.compositeDeviceId === node?.parentGroupId
				? "removeFromCompositeDevice"
				: "removeFromGroup";

		getSelectedMenuItem(menuItem, node);
	};

	const handleDeviceSelection = () => {
		if (justDraggedRef.current) {
			return;
		}

		if (allowDeviceSelection && node?.entityType === "device") {
			setSelectedDevice(node);
			getSelectedNodes(node?.id);
		} else if (
			isForFacilitiesFloorPlan &&
			(node?.entityType === "device" ||
				node?.entityType === "camera" ||
				node?.entityType === "compositeDevice") &&
			(!node?.compositeDeviceId || node?.compositeDeviceId !== node?.parentGroupId)
		) {
			setSelectedDevice(node);
		} else if (node?.isDevice) {
			getSelectedMenuItem("openItem", node);
		}
	};

	const textRef = useRef(null);
	const textObserver = useRef(null);
	const [isOverflowing, setIsOverflowing] = useState(false);

	useEffect(() => {
		const el = textRef.current;
		if (!el) return;

		if (textObserver.current) {
			textObserver.current.disconnect();
		}

		const observer = new ResizeObserver(() => {
			setIsOverflowing(el.scrollWidth > el.clientWidth);
		});
		observer.observe(el);
		textObserver.current = observer;

		return () => observer.disconnect();
	}, [node?.name]);

	const getDeviceGeometry = node => {
		if (node?.compositeDeviceId && compositeDevices?.[node?.compositeDeviceId]?.entityData?.geometry) {
			return compositeDevices[node.compositeDeviceId].entityData.geometry;
		}
		return node?.entityData?.geometry;
	};

	return (
		<div
			{...getDeviceGroupDropDataset(node)}
			onMouseEnter={() => setHoveredNodeId(node?.id)}
			onMouseLeave={() => setHoveredNodeId(null)}
			onPointerDown={isDraggableDevice ? handleDeviceHandlePointerDown : undefined}
			onClick={handleDeviceSelection}
			style={{
				...styles.container(node, hoveredNodeId, selectedDevice, displayChevrons),
				...(isDraggableDevice ? { userSelect: "none" } : {})
			}} data-testid="orion-components-device-manager-device-tree-components-render-label-with-icon-div"
		>
			{selectedNodeForEdit !== node?.id ? (
				<>
					{node?.children?.length > 0 && displayChevrons && (
						<div style={styles.chevron(dir)} onClick={handleChevronClick} data-testid="orion-components-device-manager-device-tree-components-render-label-with-icon-div-2">
							{isExpanded ? (
								<ChevronDown />
							) : dir === "rtl" ? (
								<ChevronLeft style={{ marginTop: 8, width: 20, height: 24 }} />
							) : (
								<ChevronRight />
							)}
						</div>
					)}

					{addToDeviceGroup && node?.id !== "root" && (
						<div style={styles.checkbox(node, dir)}>
							<CustomizedCheckBox
								checked={isGroupSelected}
								onClick={e => {
									selectDeviceGroups(e);
								}} data-testid="orion-components-device-manager-device-tree-components-render-label-with-icon-customized-check-box"
							/>
						</div>
					)}

					{node?.isGroup && (
						<div style={styles.folder(node, dir)}>
							<SvgIcon style={styles.svgIconWrapper}>
								<Folder />
							</SvgIcon>
						</div>
					)}

					{isDraggableDevice && (
						<div
							onPointerDown={handleDragHandlePointerDown}
							draggable={false}
							onClick={handleDragHandleClick}
							aria-label={dragHandleTitle}
							style={styles.dragHandle(dir)}
							title={dragHandleTitle} data-testid="orion-components-device-manager-device-tree-components-render-label-with-icon-div-3"
						>
							<span style={styles.dragHandleIcon} draggable={false}>
								<DragIndicatorIcon fontSize="inherit" />
							</span>
						</div>
					)}

					{hasPendingPlacement && <span style={styles.placementPendingIndicator(dir)} />}

					{node?.isDevice && !isForFacilitiesFloorPlan ? (
						<>
							<div style={{ marginInlineStart: 1 }} draggable={false}>
								<TargetingIcon
									feedId={node?.feedId}
									id={node?.id}
									geometry={getDeviceGeometry(node)}
									iconContainerStyle={styles.targetingIcon(dir)}
									customStyles={{ width: "18px", height: "18px" }}
								/>
							</div>
						</>
					) : (
						<></>
					)}

					{node?.isDevice && (
						<div style={styles.spriteIcon(node, dir)} draggable={false}>
							<SpriteIcon iconName={node?.icon} customWidth="10px" />
						</div>
					)}

					<Tooltip
						title={isOverflowing ? node?.name : ""}
						disableHoverListener={!isOverflowing}
						disableInteractive data-testid="orion-components-device-manager-device-tree-components-render-label-with-icon-tooltip"
					>
						<Typography
							ref={textRef}
							draggable={false}
							sx={{
								color: "#FFFFFF",
								"&.MuiTypography-body1": {
									margin: "0px !important"
								},
								textOverflow: "ellipsis",
								overflow: "hidden",
								whiteSpace: "nowrap",
								minWidth: 0,
								flexGrow: 1,
								userSelect: "none"
							}}
						>
							{node?.name}
						</Typography>
					</Tooltip>
					{node?.id === hoveredNodeId &&
						!readOnly &&
						!isForFacilitiesFloorPlan &&
						node?.isGroup &&
						canManageCamerasApp && (
							<div style={styles.nodeOperations(dir)}>
								<div style={styles.editButton(dir)} onClick={handleEditClick} data-testid="orion-components-device-manager-device-tree-components-render-label-with-icon-div-4">
									<Edit />
								</div>
								<div style={{ cursor: "pointer" }} onClick={handleAddNewClick} data-testid="orion-components-device-manager-device-tree-components-render-label-with-icon-div-5">
									<AddNew />
								</div>
							</div>
						)}

					{((node?.isDevice && node?.parentGroupId !== "root") ||
						(node?.entityType === "compositeDevice" && node?.parentGroupId !== "root")) &&
						node?.id === hoveredNodeId &&
						!readOnly &&
						!isForFacilitiesFloorPlan && (
							<div style={styles.nodeOperations(dir)}>
								<div style={{ cursor: "pointer" }} onClick={() => handleRemove(node)} data-testid="orion-components-device-manager-device-tree-components-render-label-with-icon-div-6">
									<Tooltip
										title={getTranslation(
											"global.deviceManager.deviceTree.components.rightClickMenu.removeFromGroup"
										)}
										placement="top" data-testid="orion-components-device-manager-device-tree-components-render-label-with-icon-tooltip-2"
									>
										<Delete />
									</Tooltip>
								</div>
							</div>
						)}
				</>
			) : (
				<EditGroupNode
					node={node}
					clearSelectedNodeForEdit={clearSelectedNodeForEdit}
					addOrUpdateNodeLabel={addOrUpdateNodeLabel}
				/>
			)}
		</div>
	);
};

RenderLabelWithIcon.propTypes = {
	node: PropTypes.shape({
		id: PropTypes.string,
		name: PropTypes.string,
		level: PropTypes.number,
		parentLevel: PropTypes.number,
		isDevice: PropTypes.bool,
		isGroup: PropTypes.bool,
		entityType: PropTypes.string,
		feedId: PropTypes.string,
		entityData: PropTypes.shape({
			geometry: PropTypes.object
		}),
		icon: PropTypes.string,
		deviceStateColor: PropTypes.string,
		children: PropTypes.array
	}),
	isExpanded: PropTypes.bool,
	hoveredNodeId: PropTypes.string,
	setHoveredNodeId: PropTypes.func.isRequired,
	selectedNodeForEdit: PropTypes.string,
	setSelectedNodeForEdit: PropTypes.func,
	addOrUpdateNodeLabel: PropTypes.func,
	addNewNode: PropTypes.func,
	readOnly: PropTypes.bool,
	addToDeviceGroup: PropTypes.bool,
	getSelectedNodes: PropTypes.func.isRequired,
	isForFacilitiesFloorPlan: PropTypes.bool,
	selectedDevice: PropTypes.object,
	setSelectedDevice: PropTypes.func,
	getSelectedDevice: PropTypes.func,
	displayChevrons: PropTypes.bool,
	getSelectedMenuItem: PropTypes.func,
	parentCount: PropTypes.number,
	onDeviceDragStart: PropTypes.func,
	onDeviceDragEnd: PropTypes.func,
	isTreeItemDraggable: PropTypes.bool,
	onDeviceDropToGroup: PropTypes.func,
	onDropTargetHoverChange: PropTypes.func,
	onChevronToggle: PropTypes.func,
	enableDevicePlacementDrag: PropTypes.bool,
	enableDeviceGroupDrag: PropTypes.bool
};
RenderLabelWithIcon.defaultProps = {
	node: {
		id: "",
		name: "",
		level: 0,
		parentLevel: 0,
		isDevice: false,
		isGroup: false,
		entityType: "",
		feedId: "",
		entityData: { geometry: {} },
		icon: "",
		deviceStateColor: "#6A6C70",
		children: []
	},
	isExpanded: false,
	hoveredNodeId: null,
	selectedNodeForEdit: null,
	readOnly: false,
	addToDeviceGroup: false,
	isForFacilitiesFloorPlan: false,
	selectedDevice: "",
	setSelectedDevice: () => {},
	getSelectedDevice: () => {},
	displayChevrons: true,
	getSelectedMenuItem: () => {},
	parentCount: 0,
	onDeviceDragStart: undefined,
	onDeviceDragEnd: undefined,
	isTreeItemDraggable: false,
	onDeviceDropToGroup: undefined,
	onDropTargetHoverChange: undefined,
	enableDevicePlacementDrag: false,
	enableDeviceGroupDrag: false
};

export default RenderLabelWithIcon;
